/**
 * CommitBoard — the import session's overview AND its commit surface
 * (GDN-2253 / GDN-2256).
 *
 * One table, one row per resource type, ordered by dependency. The ordering
 * is server-derived: the staging summary's `waits_for` maps each type to the
 * session types its rows reference (meds → patients, documents → encounters),
 * so a new resource type slots itself in with no code change here. Each row
 * shows what was ingested (staged / committed / skipped), what still needs a
 * human (decisions, provider mapping), and carries its own Commit button — a
 * type commits only when its own gates pass AND everything it waits for is
 * fully committed. The disabled button's tooltip always says why.
 *
 * Every row is also a door: clicking it opens the Review surface scoped to
 * that type (the host owns that navigation).
 *
 * Presentational: the host supplies rows + waits_for and dispatches commits.
 */
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import {
  Box,
  Button,
  Chip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

export interface CommitBoardRow {
  resourceType: string;
  staged: number;
  committed: number;
  skipped: number;
  invalid: number;
  /** Undecided conflicts of this type (patients also count open matches). */
  openDecisions: number;
  /** Unmapped required providers — only ever set on encounter rows. */
  unmappedProviders?: number;
}

export interface CommitBoardProps {
  rows: CommitBoardRow[];
  /**
   * Source providers, rendered as a first-class board row (they're a task
   * with a gate, like any type): "N to map" chips and a door into the
   * mapping surface. Absent = no provider row (read-only contexts).
   */
  providers?: { total: number; unmapped: number };
  onOpenProviders?: () => void;
  /**
   * Server-derived dependencies: type -> the session types it must wait for
   * at commit (the staging summary's waits_for, GDN-2256).
   */
  waitsFor: Record<string, string[]>;
  /** Commit one type now (same engine run, scoped to the type). */
  onCommitType: (resourceType: string) => void;
  /** Open the Review surface scoped to this type. */
  onOpenType: (resourceType: string) => void;
  commitInFlight: boolean;
}

const typeLabel = (rt: string) => rt.replaceAll('_', ' ');

/** Why a type can't commit right now, or null when it can. */
function gateReason(
  row: CommitBoardRow,
  rows: CommitBoardRow[],
  waitsFor: Record<string, string[]>,
): string | null {
  if (row.staged === 0) return 'Nothing staged';
  if (row.openDecisions > 0)
    return `${row.openDecisions.toLocaleString()} decision${
      row.openDecisions > 1 ? 's' : ''
    } outstanding`;
  if ((row.unmappedProviders ?? 0) > 0)
    return `${row.unmappedProviders} provider${
      (row.unmappedProviders ?? 0) > 1 ? 's' : ''
    } unmapped`;
  const blocking = (waitsFor[row.resourceType] ?? [])
    .map((parent) => rows.find((r) => r.resourceType === parent))
    .find((parent) => parent && parent.staged > 0);
  if (blocking)
    return `Commit ${typeLabel(blocking.resourceType)} rows first (${blocking.staged.toLocaleString()} still staged)`;
  return null;
}

/**
 * Dependency order: types sort after everything they (transitively) wait
 * for; ties break alphabetically so the board is stable. `path` guards
 * cycles along the CURRENT chain only — each sibling branch gets its own
 * copy, so a diamond (two parents sharing an ancestor) still computes the
 * ancestor's real depth instead of short-circuiting to 0.
 */
function depthOf(
  rt: string,
  waitsFor: Record<string, string[]>,
  path: Set<string> = new Set(),
): number {
  if (path.has(rt)) return 0; // cycle guard — data error, don't hang
  const parents = waitsFor[rt] ?? [];
  if (parents.length === 0) return 0;
  const next = new Set(path);
  next.add(rt);
  return 1 + Math.max(...parents.map((p) => depthOf(p, waitsFor, next)));
}

export function CommitBoard({
  rows,
  providers,
  onOpenProviders,
  waitsFor,
  onCommitType,
  onOpenType,
  commitInFlight,
}: CommitBoardProps) {
  const sorted = [...rows].sort(
    (a, b) =>
      depthOf(a.resourceType, waitsFor) - depthOf(b.resourceType, waitsFor) ||
      a.resourceType.localeCompare(b.resourceType),
  );
  return (
    <Table size="small" sx={{ mb: 3 }}>
      <TableHead>
        <TableRow>
          <TableCell>Resource type</TableCell>
          <TableCell align="right">staged</TableCell>
          <TableCell align="right">committed</TableCell>
          <TableCell align="right">skipped</TableCell>
          <TableCell>Waits for</TableCell>
          <TableCell>Needs a human</TableCell>
          <TableCell align="right" />
        </TableRow>
      </TableHead>
      <TableBody>
        {providers && providers.total > 0 && (
          <TableRow
            hover
            sx={{ cursor: 'pointer' }}
            onClick={() => onOpenProviders?.()}
          >
            <TableCell>
              <Link
                component="button"
                underline="hover"
                color="inherit"
                sx={{ font: 'inherit' }}
              >
                source providers
              </Link>
            </TableCell>
            <TableCell align="right">{providers.total.toLocaleString()}</TableCell>
            <TableCell align="right">—</TableCell>
            <TableCell align="right">—</TableCell>
            <TableCell sx={{ color: 'text.secondary' }}>—</TableCell>
            <TableCell>
              {providers.unmapped > 0 ? (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={`${providers.unmapped} to map`}
                />
              ) : (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  icon={<CheckCircleOutlinedIcon fontSize="small" />}
                  label="Mapped"
                />
              )}
            </TableCell>
            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
              <Button size="small" onClick={() => onOpenProviders?.()}>
                Map
              </Button>
            </TableCell>
          </TableRow>
        )}
        {sorted.map((row) => {
          const reason = gateReason(row, sorted, waitsFor);
          const done = row.staged === 0 && row.committed > 0;
          const parents = waitsFor[row.resourceType] ?? [];
          return (
            <TableRow
              key={row.resourceType}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => onOpenType(row.resourceType)}
            >
              <TableCell sx={{ textTransform: 'capitalize' }}>
                <Link
                  component="button"
                  underline="hover"
                  color="inherit"
                  sx={{ font: 'inherit' }}
                >
                  {typeLabel(row.resourceType)}
                </Link>
                {row.invalid > 0 && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="error"
                    sx={{ ml: 1 }}
                  >
                    {row.invalid} unreadable
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                {row.staged.toLocaleString()}
              </TableCell>
              <TableCell align="right">
                {row.committed.toLocaleString()}
              </TableCell>
              <TableCell align="right">
                {row.skipped.toLocaleString()}
              </TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>
                {parents.length > 0 ? parents.map(typeLabel).join(', ') : '—'}
              </TableCell>
              <TableCell>
                {row.openDecisions > 0 ? (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`${row.openDecisions.toLocaleString()} to decide`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenType(row.resourceType);
                    }}
                  />
                ) : (row.unmappedProviders ?? 0) > 0 ? (
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={`${row.unmappedProviders} provider${
                      (row.unmappedProviders ?? 0) > 1 ? 's' : ''
                    } to map`}
                  />
                ) : null}
              </TableCell>
              <TableCell
                align="right"
                sx={{ whiteSpace: 'nowrap' }}
                onClick={(e) => e.stopPropagation()}
              >
                {done ? (
                  <Chip
                    size="small"
                    color="success"
                    icon={<CheckCircleOutlinedIcon fontSize="small" />}
                    label="Committed"
                  />
                ) : (
                  <Tooltip title={reason ?? ''}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={commitInFlight || reason !== null}
                        onClick={() => onCommitType(row.resourceType)}
                      >
                        Commit
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          );
        })}
        {sorted.length === 0 && (
          <TableRow>
            <TableCell colSpan={7}>
              <Box sx={{ py: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Nothing staged yet.
                </Typography>
              </Box>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
